import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { Promisable } from 'type-fest'
import FluentThumbDislike20Filled from '~icons/fluent/thumb-dislike-20-filled'
import FluentThumbLike20Filled from '~icons/fluent/thumb-like-20-filled'
import type { Review } from 'src/services/apollo/generated/graphql'
import { ReviewRating } from 'src/services/apollo/generated/graphql'
import { submitModelItemReview } from 'src/services/apollo/review'
import { cx } from 'src/utils/cx'
import { formatNum } from 'src/utils/formatNum'
import { track } from 'src/utils/tracker'

export default function Rating(props: StyleProps & { value?: number }) {
  const { t } = useTranslation(['common'])

  return (
    <span
      key={props.value}
      className={cx('inline-flex flex-col', props.className)}
      style={props.style}
    >
      {t('common:review.rating-score.label', { context: props.value ?? 0 })}
    </span>
  )
}

export function RatingInput(
  props: StyleProps & {
    itemId: string
    value?: ReviewRating
    onSubmit?: (review: Review | undefined) => Promisable<void>
    positiveCount?: number
    negativeCount?: number
  },
) {
  const [submitting, setSubmitting] = useState(false)
  // Keeping optimistic value as apollo's cache update doesn't reflect
  // when the rating is canceled
  // Remove this optimistic value when apollo's cache update is fixed
  const [optimisticValue, setOptimisticValue] = useState<ReviewRating>()

  if (!submitting && optimisticValue !== props.value) {
    setOptimisticValue(props.value)
  }

  async function submit(clickedValue: ReviewRating) {
    const cancelling = props.value === clickedValue
    track('model_review_click', {
      value: clickedValue,
      modelId: props.itemId,
      isCancel: cancelling,
    })

    setSubmitting(true)
    setOptimisticValue(cancelling ? undefined : clickedValue)
    const review = await submitModelItemReview(props.itemId, {
      rating: cancelling ? ReviewRating.Cancel : clickedValue,
    })
    // Wait for possible `props.value` updating
    await props.onSubmit?.(review)
    setSubmitting(false)
  }

  const classes = {
    button:
      'flex justify-center items-center gap-1 px-2 py-0.5 text-base disabled:opacity-50 transition',
  } as const

  return (
    <span
      className={cx(
        'overflow-hidden inline-grid grid-cols-2 bg-white/50 dark:bg-black/40 ring-1 ring-black/20 dark:ring-black/50 divide-x divide-black/20 dark:divide-black/50 rounded-md',
        props.className,
      )}
      style={props.style}
    >
      <button
        type="button"
        disabled={submitting}
        className={cx(
          classes.button,
          optimisticValue === ReviewRating.Like
            ? 'text-white bg-green-600'
            : 'text-green-600',
        )}
        onClick={() => submit(ReviewRating.Like)}
      >
        <FluentThumbLike20Filled className="my-0.5 size-5" />
        {Number(props.positiveCount) > 0 && formatNum(props.positiveCount)}
      </button>
      <button
        type="button"
        disabled={submitting}
        className={cx(
          classes.button,
          optimisticValue === ReviewRating.Dislike
            ? 'text-white bg-red-600'
            : 'text-red-600',
        )}
        onClick={() => submit(ReviewRating.Dislike)}
      >
        <FluentThumbDislike20Filled className="my-0.5 size-5" />
        {Number(props.negativeCount) > 0 && formatNum(props.negativeCount)}
      </button>
    </span>
  )
}
